import React from 'react'
import { Table, Tag } from 'antd'
import EpcCycleIcon from './EpcCycleIcons'
import {
  ACTION_CONFIG,
  STAGE_CONFIG,
  STORE_DIRS,
  colorVars,
  dirsOf,
  eventsOf,
  isIoStage,
  isStoreStage,
  sortNewestFirst,
  stageColor
} from './epcCycleUtils'

const EMPTY = '-'
const show = v => (v === null || v === undefined || v === '' ? EMPTY : v)

export const dirLabel = (t, dir) => t(`dir.${String(dir).toLowerCase()}`, null, dir)

/**
 * 只有一个方向时显示「DC IB」「Store CycleCount」，多个方向时只显示环节名
 */
export const stageName = (t, type, dirs = []) => {
  const name = t(`stage.${type}`, null, type)
  if ((isIoStage(type) || isStoreStage(type)) && dirs.length === 1) {
    return `${name} ${dirLabel(t, dirs[0])}`
  }
  return name
}

const detailText = (t, key, value) => {
  if (key === 'type' && value) return dirLabel(t, value)
  return show(value)
}

const actionLabel = (t, kind) => t(`event.${ACTION_CONFIG[kind].localeKey}`, null, kind)

const eventSummary = (t, ev) => (
  <span>
    {ev.time || EMPTY} · {t('field.plant')} {show(ev.plant)}
  </span>
)

const KvRows = ({ rows }) => (
  <div className="kv">
    {rows.map((r, i) => (
      <div className="row" key={i}>
        <span className="k">{r[0]}</span>
        <span className="v">{r[1]}</span>
      </div>
    ))}
  </div>
)

const ActionText = ({ t, events }) =>
  events.length ? (
    <span>
      {events.map((ev, i) => (
        <span key={i} style={{ color: ACTION_CONFIG[ev.kind].color, fontWeight: 600 }}>
          {i > 0 ? ', ' : ''}
          {actionLabel(t, ev.kind)}
        </span>
      ))}
    </span>
  ) : (
    EMPTY
  )

/**
 * 多条记录表格：按时间倒序，最新一行高亮；明细字段合并为列，缺失显示 -
 */
const RecordTable = ({ t, rows, showStep, showSource, showActions }) => {
  const sorted = sortNewestFirst(rows)
  const detailKeys = []
  sorted.forEach(r =>
    (r.details || []).forEach(d => {
      if (!detailKeys.includes(d.key)) detailKeys.push(d.key)
    })
  )
  const columns = [
    { title: '#', key: 'seq', width: 44, render: (_, __, k) => <span className="seq">{sorted.length - k}</span> },
    { title: <span className="sorted">{t('field.time')}</span>, dataIndex: 'time', key: 'time', render: show },
    ...(showStep ? [{ title: t('field.step'), key: 'step', render: (_, r) => r.n + 1 }] : []),
    { title: t('field.operation'), dataIndex: 'operation', key: 'operation', render: show },
    ...(showSource ? [{ title: t('field.sourceEpc'), dataIndex: 'sourceEpc', key: 'sourceEpc', render: show }] : []),
    ...detailKeys.map(key => ({
      title: t(`field.${key}`, null, key),
      key,
      render: (_, r) => {
        const d = (r.details || []).find(x => x.key === key)
        return detailText(t, key, d && d.value)
      }
    })),
    ...(showActions
      ? [
          {
            title: t('field.actions'),
            key: 'actions',
            render: (_, r) => <ActionText t={t} events={r.events || []} />
          }
        ]
      : [])
  ]
  return (
    <Table
      className="ptable"
      size="small"
      rowKey={(_, k) => k}
      columns={columns}
      dataSource={sorted}
      pagination={false}
      scroll={{ x: 'max-content', y: 320 }}
      rowClassName={(_, k) => (k === 0 ? 'latest' : '')}
    />
  )
}

const recordRows = (t, record, mainEpc) => [
  [t('field.time'), show(record.time)],
  [t('field.operation'), show(record.operation)],
  ...(record.sourceEpc && record.sourceEpc !== mainEpc ? [[t('field.sourceEpc'), record.sourceEpc]] : []),
  ...(record.details || []).map(d => [t(`field.${d.key}`, null, d.key), detailText(t, d.key, d.value)])
]

const eventRows = (t, events) =>
  events.map((ev, i) => [
    <span key={`span-${i}`} style={{ color: ACTION_CONFIG[ev.kind].color, fontWeight: 700 }}>
      {actionLabel(t, ev.kind)}
    </span>,
    eventSummary(t, ev)
  ])

const RecordsBlock = ({ t, rows, events, mainEpc, multiStage }) => {
  if (rows.length > 1) {
    return (
      <RecordTable
        t={t}
        rows={rows}
        showStep={multiStage}
        showSource={rows.some(r => r.sourceEpc && r.sourceEpc !== mainEpc)}
        showActions={rows.some(r => (r.events || []).length)}
      />
    )
  }
  return <KvRows rows={[...recordRows(t, rows[0], mainEpc), ...eventRows(t, events)]} />
}

const collect = (stages, list, filter, eventFilter) =>
  list.flatMap(n => {
    const stage = stages[n]
    const recs = (stage.records || []).filter(filter)
    const evs = (stage.events || []).filter(eventFilter)
    return recs.map((r, k) => ({ ...r, n, events: k === recs.length - 1 ? evs : [] }))
  })

const StageBody = ({ t, card, list }) => {
  const stages = card.stages
  const type = stages[list[0]].stageType
  const multiStage = list.length > 1
  if (isIoStage(type) || isStoreStage(type)) {
    const order = isStoreStage(type) ? [...STORE_DIRS].reverse() : ['OB', 'IB']
    const storeEvents = isStoreStage(type) ? list.flatMap(n => stages[n].events || []) : []
    const sections = order.map(dir => {
      const rows = collect(
        stages,
        list,
        r => r.direction === dir,
        ev => (isIoStage(type) ? ev.direction === dir : false)
      )
      const events = rows.flatMap(r => r.events || [])
      const label = dirLabel(t, dir)
      const full = dir === 'IB' ? t('dir.inbound') : dir === 'OB' ? t('dir.outbound') : ''
      return (
        <div className={`io-sec${rows.length ? '' : ' none'}`} key={dir}>
          <div className="io-sec-h">
            <IoPill dirs={[dir]} all={[dir]} />
            <span>
              {t(`stage.${type}`, null, type)} {label}
            </span>
            {full ? <span className="muted">{full}</span> : null}
            <span className="n">
              {rows.length ? t('records', { n: rows.length }) : t('dir.noRecord', { dir: label })}
            </span>
          </div>
          {rows.length ? (
            <div className="io-sec-b">
              <RecordsBlock t={t} rows={rows} events={events} mainEpc={card.epc} multiStage={multiStage} />
            </div>
          ) : null}
        </div>
      )
    })
    return (
      <>
        {sections}
        {storeEvents.length ? <KvRows rows={eventRows(t, storeEvents)} /> : null}
      </>
    )
  }
  const rows = collect(
    stages,
    list,
    () => true,
    () => true
  )
  const events = rows.flatMap(r => r.events || [])
  if (!rows.length) return <div className="muted">{EMPTY}</div>
  return <RecordsBlock t={t} rows={rows} events={events} mainEpc={card.epc} multiStage={multiStage} />
}

const stageOf = (t, card, n, ev) => {
  const s = card.stages[n]
  const dirs = ev.direction ? [ev.direction] : s.directions || []
  return `${stageName(t, s.stageType, dirs)} · ${show(s.location)}`
}

const EpcList = ({ t, card, epcs }) => (
  <div>
    {(epcs || []).map(epc => (
      <div key={epc} className="mono">
        {epc}
        {epc === card.epc ? (
          <Tag className="tag-main" bordered={false}>
            {t('main')}
          </Tag>
        ) : null}
      </div>
    ))}
  </div>
)

const reworkTypeText = (t, v) => (v === null || v === undefined ? EMPTY : t(`reworkType.${v}`, null, String(v)))

const EventBody = ({ t, card, list, kind }) => {
  const evs = eventsOf(card.stages, list, kind)
  const rework = kind === 'REWORK'
  const reprint = kind === 'REPRINT'
  if (evs.length > 1) {
    const rows = evs.map(({ n, ev }) => ({ ...ev, n }))
    const sorted = sortNewestFirst(rows)
    const columns = [
      { title: '#', key: 'seq', width: 44, render: (_, __, k) => <span className="seq">{sorted.length - k}</span> },
      { title: <span className="sorted">{t('field.time')}</span>, dataIndex: 'time', key: 'time', render: show },
      { title: t('field.step'), key: 'step', render: (_, r) => r.n + 1 },
      { title: t('field.stage'), key: 'stage', render: (_, r) => stageOf(t, card, r.n, r) },
      { title: t('field.plant'), key: 'plant', render: (_, r) => eventSummary(t, { ...r, time: null }) },
      ...(rework
        ? [
            { title: t('field.cartonNo'), dataIndex: 'cartonNo', key: 'cartonNo', render: show },
            { title: t('field.reworkType'), key: 'reworkType', render: (_, r) => reworkTypeText(t, r.reworkType) },
            { title: t('field.epcs'), key: 'epcs', render: (_, r) => <EpcList t={t} card={card} epcs={r.epcs} /> }
          ]
        : reprint
        ? [
            { title: t('field.operationType'), dataIndex: 'reprintOperateType', key: 'operationType', render: show },
            { title: t('field.status'), dataIndex: 'reprintStatus', key: 'status', render: show },
            { title: 'TID', dataIndex: 'reprintTid', key: 'tid', render: show }
          ]
        : [])
    ]
    return (
      <Table
        className="ptable"
        size="small"
        rowKey={(_, k) => k}
        columns={columns}
        dataSource={sorted}
        pagination={false}
        scroll={{ x: 'max-content', y: 320 }}
        rowClassName={(_, k) => (k === 0 ? 'latest' : '')}
      />
    )
  }
  if (!evs.length) return null
  const { n, ev } = evs[0]
  const rows = [
    [t('field.stage'), stageOf(t, card, n, ev)],
    [t('field.time'), show(ev.time)],
    [t('field.plant'), eventSummary(t, { ...ev, time: null })]
  ]
  if (rework) {
    rows.push([t('field.cartonNo'), show(ev.cartonNo)])
    rows.push([t('field.reworkType'), reworkTypeText(t, ev.reworkType)])
    rows.push([
      `${t('field.epcs')} (${(ev.epcs || []).length})`,
      <EpcList key="epcs-list" t={t} card={card} epcs={ev.epcs} />
    ])
  } else if (reprint) {
    rows.push([t('field.operationType'), show(ev.reprintOperateType)])
    rows.push([t('field.status'), show(ev.reprintStatus)])
    rows.push(['TID', show(ev.reprintTid)])
  }
  return <KvRows rows={rows} />
}

export const IoPill = ({ dirs, all, mini }) => (
  <span className={`io${mini ? ' mini' : ''}`}>
    {(all || ['IB', 'OB']).map(d => (
      <span key={d} className={dirs.includes(d) ? 'on' : ''}>
        {d === 'CYCLECOUNT' ? 'CC' : d}
      </span>
    ))}
  </span>
)

/**
 * 浮层内容：环节（kind 为空）或 Rework / Reprint（kind = REWORK / REPRINT）
 */
const EpcCyclePopover = ({ t, card, list, kind, onClose }) => {
  const stages = card.stages
  const first = stages[list[0]]
  const type = first.stageType
  let color
  let icon
  let title
  let sub
  let wide
  let body
  if (kind) {
    const evs = eventsOf(stages, list, kind)
    color = ACTION_CONFIG[kind].color
    icon = ACTION_CONFIG[kind].icon
    title = actionLabel(t, kind)
    sub = evs.length > 1 ? `${t('records', { n: evs.length })} · ${t('latestFirst')}` : t('records', { n: evs.length })
    wide = evs.length > 1
    body = <EventBody t={t} card={card} list={list} kind={kind} />
  } else {
    const dirs = dirsOf(stages, list)
    const recordCount = list.reduce((sum, n) => sum + (stages[n].records || []).length, 0)
    color = stageColor(type)
    icon = STAGE_CONFIG[type] ? STAGE_CONFIG[type].icon : 'dc'
    title = t(`stage.${type}`, null, type)
    const dirText = dirs.map(d => dirLabel(t, d)).join(' + ')
    sub =
      list.length > 1
        ? `${t('visits', { n: list.length })}${dirText ? ' · ' + dirText : ''} · ${t('latestFirst')}`
        : `${t('step', { n: list[0] + 1 })} · ${show(first.location)}${dirText ? ' · ' + dirText : ''}`
    wide = recordCount > 1 || list.length > 1
    body = <StageBody t={t} card={card} list={list} />
  }
  const vars = color.startsWith('#') ? colorVars(color) : { '--c': color }
  return (
    <div className={`epc-pop${wide ? ' wide' : ''}`} style={vars}>
      <div className="pop-head">
        <span className="pop-ico">
          <EpcCycleIcon name={icon} />
        </span>
        <div>
          <div className="t">{title}</div>
          <div className="s">{sub}</div>
        </div>
        <span className="x" onClick={onClose}>
          ✕
        </span>
      </div>
      <div className="pop-body">{body}</div>
    </div>
  )
}

export default EpcCyclePopover
